/**
 * Cliente HTTP do painel.
 *
 * Fino de propósito: a API é servida pelo mesmo host (o Caddy faz proxy de
 * /api), então não há CORS nem base URL para configurar.
 */

export interface ErroApi {
  codigo: string;
  mensagem: string;
  status: number;
  /** Preenchido quando a validação zod recusa campos. */
  erros?: { campo: string; mensagem: string }[];
  /** Preenchido em 403 por falta de permissão. */
  permissoesFaltantes?: string[];
}

export class ApiError extends Error implements ErroApi {
  codigo: string;
  status: number;
  erros?: { campo: string; mensagem: string }[];
  permissoesFaltantes?: string[];

  /**
   * Duplica `message` com o nome em português.
   *
   * `Error.message` é fixo da plataforma, mas o resto do código lê `mensagem` —
   * manter os dois evita ter que lembrar qual é qual no meio de um componente.
   */
  readonly mensagem: string;

  constructor(dados: ErroApi) {
    super(dados.mensagem);
    this.name = 'ApiError';
    this.mensagem = dados.mensagem;
    this.codigo = dados.codigo;
    this.status = dados.status;
    this.erros = dados.erros;
    this.permissoesFaltantes = dados.permissoesFaltantes;
  }

  /**
   * Texto pronto para exibir ao operador.
   *
   * Uma recusa de validação chega com `mensagem` genérica e o motivo real em
   * `erros` — mostrar só `mensagem` esconde exatamente a parte que diz o que
   * corrigir, e a tela vira "não deu certo" sem explicação.
   */
  get descricao(): string {
    if (!this.erros?.length) return this.mensagem;
    return this.erros.map((e) => e.mensagem).join(' ');
  }

  /** Distingue "perdeu o quorum" de erro comum, para a interface reagir. */
  get semQuorum(): boolean {
    return this.codigo === 'CLUSTER_SEM_QUORUM';
  }

  get naoAutenticado(): boolean {
    return this.status === 401;
  }
}

const BASE = '/api';

async function requisitar<T>(metodo: string, caminho: string, corpo?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${caminho}`, {
    method: metodo,
    // Sempre: a sessão vive num cookie httpOnly, que só acompanha a requisição
    // quando as credenciais são incluídas explicitamente.
    credentials: 'same-origin',
    headers: corpo ? { 'Content-Type': 'application/json' } : undefined,
    body: corpo !== undefined ? JSON.stringify(corpo) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const texto = await res.text();
  const dados: unknown = texto ? JSON.parse(texto) : {};

  if (!res.ok) {
    throw new ApiError({ ...extrairErro(dados), status: res.status });
  }

  return dados as T;
}

/** Corpo de erro como o Nest o devolve. */
interface CorpoErro {
  codigo?: unknown;
  mensagem?: unknown;
  erros?: unknown;
  permissoesFaltantes?: unknown;
  /** O Nest embrulha o objeto da exceção aqui quando ela recebe um objeto. */
  message?: CorpoErro | string;
}

/**
 * Normaliza as duas formas de erro que a API pode devolver.
 *
 * Quando uma exceção do Nest recebe um objeto, ele aparece aninhado em
 * `message`; quando o corpo é montado à mão, vem na raiz. Tratar só uma das
 * formas produziria "undefined" na tela em metade dos casos.
 */
function extrairErro(dados: unknown): Omit<ErroApi, 'status'> {
  const raiz = (dados ?? {}) as CorpoErro;
  const aninhado = typeof raiz.message === 'object' && raiz.message !== null ? raiz.message : {};

  const codigo = raiz.codigo ?? aninhado.codigo;
  const mensagem = raiz.mensagem ?? aninhado.mensagem;
  const erros = raiz.erros ?? aninhado.erros;
  const faltantes = raiz.permissoesFaltantes ?? aninhado.permissoesFaltantes;

  return {
    codigo: typeof codigo === 'string' ? codigo : 'ERRO',
    mensagem: typeof mensagem === 'string' ? mensagem : 'Não foi possível concluir a operação.',
    erros: Array.isArray(erros) ? (erros as ErroApi['erros']) : undefined,
    permissoesFaltantes: Array.isArray(faltantes) ? (faltantes as string[]) : undefined,
  };
}

export const api = {
  get: <T>(caminho: string) => requisitar<T>('GET', caminho),
  post: <T>(caminho: string, corpo?: unknown) => requisitar<T>('POST', caminho, corpo),
  put: <T>(caminho: string, corpo?: unknown) => requisitar<T>('PUT', caminho, corpo),
  patch: <T>(caminho: string, corpo?: unknown) => requisitar<T>('PATCH', caminho, corpo),
  delete: <T>(caminho: string) => requisitar<T>('DELETE', caminho),
};
